import { useCallback, useContext, useEffect, useState } from "react";
import Loader from "../../components/Loader";
import { Link, useNavigate } from "react-router-dom";
import { Box, Grid2, Tab } from "@mui/material";
import StyledGridComp from "../../components/StyledGridComp";
import { formatDate, notifyError } from "../../utilities/helper";
import { MenuContext } from "../../App";
import SearchedComponent from "../../components/SearchedComponent";
import {
  fetchAllSiteDataService,
  fetchAllSubjectDataService,
} from "../../services/subjectService";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import UncontrolledSearchSelect from "../../components/UncontrolledSearchSelect";
import useDebounceSearch from "../../hooks/useDebounceSearch";

const Subject = () => {
  const [load, setLoad] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [allData, setAllData] = useState([]);
  const [allDataLength, setAllDataLength] = useState(0);
  const [activeData, setActiveData] = useState([]);
  const [activeDataLength, setActiveDataLength] = useState(0);
  const [inactiveData, setInactiveData] = useState([]);
  const [inactiveDataLength, setInactiveDataLength] = useState(0);
  const [canAddSubject, setCanAddSubject] = useState(false);
  const [searchedValue, setSearchedValue] = useState("");
  const [tabValue, setTabValue] = useState("all");
  const [selectedSite, setSelectedSite] = useState("");
  const [siteData, setSiteData] = useState([]);

  const debounceQuery = useDebounceSearch(searchedValue, 500);

  const handleTabChange = (event, newValue) => {
    setPage(0);
    setTabValue(newValue);
  };
  const navigate = useNavigate();

  const access_token = localStorage?.getItem("access_token");

  const role = localStorage.getItem("role");
  const roleId = localStorage.getItem("roleId");
  const { systemActions } = useContext(MenuContext);

  useEffect(() => {
    if (roleId && access_token) {
      getAllSiteData(access_token, roleId);
    }
  }, [roleId, access_token]);

  useEffect(() => {
    if (page >= 0 && pageSize && access_token) {
      let activeTab = "";
      if (tabValue === "active") {
        activeTab = true;
      } else if (tabValue === "inactive") {
        activeTab = false;
      } else {
        activeTab = "";
      }
      getAllSubjectData(
        pageSize,
        page,
        access_token,
        debounceQuery,
        activeTab,
        selectedSite
      );
    }
  }, [page, pageSize, access_token, debounceQuery, tabValue, selectedSite]);

  useEffect(() => {
    if (systemActions?.length > 0 && role !== "Admin") {
      const found = systemActions?.find(
        (item) => item?.name === "CanCreatePatient"
      );
      if (found) {
        setCanAddSubject(true);
      } else {
        setCanAddSubject(false);
      }
    } else if (role === "Admin") {
      setCanAddSubject(true);
    }
  }, [systemActions, role]);

  const getAllSiteData = async (access_token, roleId) => {
    setLoad(true);
    try {
      const res = await fetchAllSiteDataService(access_token, roleId);
      if (res?.status == 200) {
        setLoad(false);
        setSiteData([{ id: "", name: "All Sites" }, ...res?.data?.result]);
        setSelectedSite("");
      }
    } catch (err) {
      setLoad(false);
      notifyError(err?.message);
      console.log(err);
      if (err?.status == 401) {
        navigate("/unauthorize");
      }
    }
  };

  const getAllSubjectData = async (
    pageSize,
    page,
    access_token,
    debouncedSearch = "",
    activeTab = "",
    selectedSite = ""
  ) => {
    setLoad(true);
    try {
      const res = await fetchAllSubjectDataService(
        page + 1,
        pageSize,
        access_token,
        debouncedSearch,
        activeTab,
        selectedSite
      );
      if (res?.status == 200) {
        setLoad(false);
        if (activeTab === true) {
          setActiveData(res?.data?.result);
          setActiveDataLength(res?.data?.totalCount);
        } else if (activeTab === false) {
          setInactiveData(res?.data?.result);
          setInactiveDataLength(res?.data?.totalCount);
        } else {
          setAllData(res?.data?.result);
          setAllDataLength(res?.data?.totalCount);
        }
      }
    } catch (err) {
      setLoad(false);
      notifyError(err?.message);
      console.log(err);
      if (err?.status == 401) {
        navigate("/unauthorize");
      }
    }
  };

  const columns = [
    {
      field: "siteNumber",
      headerName: "Site",
      width: 250,
      renderHeader: () => <div className="grid-heading">{"Site"}</div>,
      renderCell: (params) => {
        const siteNumber = params.row.siteNumber;

        return <span className="data-font">{siteNumber}</span>;
      },
    },
    {
      field: "patientNumber",
      headerName: "Subject Number",
      width: 250,
      renderHeader: () => (
        <div className="grid-heading">{"Subject Number"}</div>
      ),
      renderCell: (params) => {
        const patientNumber = params.row.patientNumber;
        const id = params.row.id;

        return (
          <button
            className="bread-crumbs-link grid-nav-btn"
            onClick={() => {
              navigate("/update-subject", {
                state: {
                  id: id,
                },
              });
            }}
          >
            {patientNumber}
          </button>
        );
      },
    },
    {
      field: "patientStatus",
      headerName: "Subject Status",
      width: 250,
      renderHeader: () => (
        <div className="grid-heading">{"Subject Status"}</div>
      ),
      renderCell: (params) => {
        const patientStatus = params.row.patientStatus;

        return <span className="data-font">{patientStatus}</span>;
      },
    },
    {
      field: "lastVisit",
      headerName: "Last Visit",
      width: 250,
      renderHeader: () => <div className="grid-heading">{"Last Visit"}</div>,
      renderCell: (params) => {
        const lastVisit = params.row.lastVisit;

        return <span className="data-font">{lastVisit}</span>;
      },
    },
    {
      field: "lastVisitDate",
      headerName: "Last Visit Date",
      width: 250,
      renderHeader: () => (
        <div className="grid-heading">{"Last Visit Date"}</div>
      ),
      renderCell: (params) => {
        const lastVisitDate = params.row.lastVisitDate;
        return (
          <span className="data-font">
            {lastVisitDate ? formatDate(lastVisitDate) : null}
          </span>
        );
      },
    },
    {
      field: "nextVisitName",
      headerName: "Next Visit",
      width: 250,
      renderHeader: () => <div className="grid-heading">{"Next Visit"}</div>,
      renderCell: (params) => {
        const id = params.row.id;
        const nextVisitName = params.row.nextVisitName;
        return (
          <span className="data-font">
            {nextVisitName ? (
              <button
                className={`grid-btn-outlined`}
                onClick={() => {
                  navigate("/visit-subject", {
                    state: {
                      id: id,
                      siteId: selectedSite,
                    },
                  });
                }}
              >
                {nextVisitName}
              </button>
            ) : (
              "N/A"
            )}
          </span>
        );
      },
    },
  ];

  const handleSearch = useCallback((e) => {
    setSearchedValue(e.target.value);
  }, []);

  return (
    <div className="content-body">
      <p className="bread-crumbs-link-container" style={{ fontWeight: "600" }}>
        <Link className="bread-crumbs-link" to="/">
          Home
        </Link>{" "}
        |{" "}
        <Link className="bread-crumbs-link" to="/subject">
          Subject Management
        </Link>
        -{" "}
        <b style={{ color: "gray" }}>{localStorage?.getItem("studyName")}</b>
      </p>

      <Grid2
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: "100%", margin: "0 auto" }}
      >
        <Grid2 item md={4}>
          <p className="page-heading">Subject</p>
        </Grid2>
        <Grid2 item md={8}>
          <div className="search-bar-container">
            <SearchedComponent
              searchedValue={searchedValue}
              handleSearch={handleSearch}
              placeholder={"Subject"}
            />
            {canAddSubject && selectedSite !== "" ? (
              <button
                className="create-btn"
                onClick={() =>
                  navigate("/add-subject", {
                    state: {
                      siteId: selectedSite,
                    },
                  })
                }
              >
                Add New Subject
              </button>
            ) : null}
          </div>
        </Grid2>
      </Grid2>

      {/* Select Content */}
      <Grid2
        container
        alignItems="center"
        justifyContent={"space-between"}
        sx={{ width: "76%", margin: "-1% 2% 2% 0%" }}
        gap={1}
      >
        <Grid2 size={{ xl: 5, md: 5, lg: 5, sm: 12, xs: 12 }}>
          <div>
            <p className="labelStyles">Site</p>

            <UncontrolledSearchSelect
              data={siteData}
              optionLabel="name"
              optionKey="id"
              noDefault={true}
              value={selectedSite}
              placeholder="Site"
              onChange={(selectedValue) => {
                setSelectedSite(selectedValue);
              }}
            />
          </div>
        </Grid2>
      </Grid2>

      {/* Tab Content */}

      <Box sx={{ width: "100%", typography: "body1" }}>
        <TabContext value={tabValue}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList
              onChange={handleTabChange}
              aria-label="lab API tabs example"
              TabIndicatorProps={{ style: { display: "none" } }}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  wordSpacing: "-2px",
                  fontWeight: 500,
                  borderRadius: "999px",
                  minHeight: "40px",
                  px: 3,
                  py: 1,
                  mx: 0.5,
                  transition: "all 0.3s ease",
                  color: "text.secondary",
                  bgcolor: "transparent",
                },
                "& .Mui-selected": {
                  bgcolor: "primary.main",
                  color: "white !important",
                  boxShadow: 2,
                },
              }}
            >
              <Tab label="Active Subjects" value="active" />
              <Tab label="Inactive Subjects" value="inactive" />
              <Tab label="All Subjects" value="all" />
            </TabList>
          </Box>
          <TabPanel value="active" sx={{ padding: "30px 0px" }}>
            {load ? (
              <div style={{ marginTop: "-100px" }}>
                <Loader />
              </div>
            ) : (
              <StyledGridComp
                rows={activeData}
                columns={columns}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                length={activeDataLength}
              ></StyledGridComp>
            )}
          </TabPanel>
          <TabPanel value="inactive" sx={{ padding: "30px 0px" }}>
            {load ? (
              <div style={{ marginTop: "-100px" }}>
                <Loader />
              </div>
            ) : (
              <StyledGridComp
                rows={inactiveData}
                columns={columns}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                length={inactiveDataLength}
              ></StyledGridComp>
            )}
          </TabPanel>
          <TabPanel value="all" sx={{ padding: "30px 0px" }}>
            {load ? (
              <div style={{ marginTop: "-100px" }}>
                <Loader />
              </div>
            ) : (
              <StyledGridComp
                rows={allData}
                columns={columns}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                length={allDataLength}
              ></StyledGridComp>
            )}
          </TabPanel>
        </TabContext>
      </Box>

      <Grid2
        alignItems={"end"}
        justifyContent="end"
        container
        sx={{ marginTop: "2%" }}
      >
        <Grid2 item md={6}>
          <button
            className="create-btn"
            onClick={() => {
              navigate("/");
            }}
          >
            Back
          </button>
        </Grid2>
      </Grid2>
    </div>
  );
};

export default Subject;
